import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import * as LucideIcons from "lucide-react";
import { AlertCircle, ArrowLeft, Check, ChevronDown, ChevronRight, Circle, Inbox, Loader, RotateCw } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { useCRM } from "@/providers/crm";
import type { BadgeValue } from "@/types/dynamic-detail";

// Building blocks of the sf_like design system for content that is not a backend `SectionConfig`
// (bespoke tab panels, tool pages). They render the same classes as `DynamicSection` and the
// record panels, so a custom tab looks like the rest of the record. See
// `.cursor/rules/frontend-design-system.mdc`.

function resolveIcon(name?: string | null) {
    if (!name) return null;
    return ((LucideIcons as unknown as Record<string, React.ComponentType<{ className?: string; strokeWidth?: number }>>)[name] ?? null) as React.ComponentType<{
        className?: string;
        strokeWidth?: number;
    }> | null;
}

/** White record panel. Panels never nest: inside a tab, use `Section` instead. */
export function Panel({ className, children, padding = "md" }: { className?: string; children: React.ReactNode; padding?: "none" | "md" }) {
    return <section className={cn("w-full rounded-md bg-white shadow-md", padding === "md" && "p-4", className)}>{children}</section>;
}

/** Gray section bar, same look as the `DynamicSection` separator. */
export function SectionBar({
    icon,
    title,
    actions,
    collapsible,
    collapsed,
    onToggle
}: {
    icon?: string;
    title: React.ReactNode;
    actions?: React.ReactNode;
    collapsible?: boolean;
    collapsed?: boolean;
    onToggle?: () => void;
}) {
    const Icon = resolveIcon(icon);
    const Title = collapsible ? "button" : "div";
    return (
        <div className="flex w-full items-center justify-between gap-2 rounded bg-gray-100 px-2 py-1.5 text-gray-700">
            <Title
                type={collapsible ? "button" : undefined}
                className="flex min-w-0 items-center gap-2 text-left"
                onClick={collapsible ? onToggle : undefined}
                aria-expanded={collapsible ? !collapsed : undefined}
            >
                {collapsible && (collapsed ? <ChevronRight className="h-4 w-4 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 flex-shrink-0" />)}
                {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
                <span className="truncate text-sm font-normal text-black">{title}</span>
            </Title>
            {actions && <div className="flex flex-shrink-0 items-center gap-2">{actions}</div>}
        </div>
    );
}

/** A titled block of a tab: section bar, then its content. Sections are spaced by `mt-4`. */
export function Section({
    icon,
    title,
    actions,
    collapsible,
    defaultCollapsed = false,
    className,
    children
}: {
    icon?: string;
    title: React.ReactNode;
    actions?: React.ReactNode;
    collapsible?: boolean;
    defaultCollapsed?: boolean;
    className?: string;
    children: React.ReactNode;
}) {
    const [collapsed, setCollapsed] = React.useState(defaultCollapsed);
    return (
        <div className={cn("mt-4 w-full first:mt-0", className)}>
            <SectionBar icon={icon} title={title} actions={actions} collapsible={collapsible} collapsed={collapsed} onToggle={() => setCollapsed(c => !c)} />
            {!collapsed && <div className="flex flex-col gap-2 p-2">{children}</div>}
        </div>
    );
}

export type FieldGridItem = {
    label: React.ReactNode;
    value: React.ReactNode;
    /** Spans the whole row, like a `large_field`. */
    large?: boolean;
    description?: string;
};

/** Two-column label/value grid with a bottom border on each row, same as `DynamicSection`. Empty values show "—". */
export function FieldGrid({ items, columns = 2 }: { items: FieldGridItem[]; columns?: 1 | 2 }) {
    return (
        <dl className="grid grid-cols-8 gap-2">
            {items.map((item, index) => {
                const wide = item.large || columns === 1;
                return (
                    <div key={index} className={cn("grid items-start border-b pb-1", wide ? "col-span-8 grid-cols-8" : "col-span-8 grid-cols-8 sm:col-span-4 sm:grid-cols-4")}>
                        <dt className="col-span-2 mt-0.5 min-w-0 truncate pr-1 text-xs text-gray-600 sm:col-span-1" title={item.description}>
                            {item.label}
                        </dt>
                        <dd className={cn("min-w-0 text-sm", wide ? "col-span-6 sm:col-span-7" : "col-span-6 sm:col-span-3")}>{isEmptyNode(item.value) ? <EmptyValue /> : item.value}</dd>
                    </div>
                );
            })}
        </dl>
    );
}

function isEmptyNode(value: React.ReactNode) {
    return value === null || value === undefined || value === "" || value === false;
}

/** A badge served by the backend as `{label, tone}`. Empty values show "—". */
export function StatusBadge({ value, className }: { value?: BadgeValue | null; className?: string }) {
    if (!value) return <EmptyValue />;
    return (
        <Badge tone={value.tone} title={value.tooltip} className={className}>
            {value.label}
        </Badge>
    );
}

/** Muted "—" for an empty value. */
export function EmptyValue() {
    return <span className="text-gray-400">—</span>;
}

/** One headline figure. `tone` carries a status only, never decoration. */
export function Metric({ label, value, hint, tone, icon }: { label: React.ReactNode; value: React.ReactNode; hint?: React.ReactNode; tone?: "success" | "warning" | "danger"; icon?: string }) {
    const Icon = resolveIcon(icon);
    return (
        <div className="flex min-w-0 flex-col gap-0.5 rounded-sm border bg-white px-3 py-2">
            <span className="flex items-center gap-1 truncate text-xs text-gray-600">
                {Icon && <Icon className="h-3.5 w-3.5 flex-shrink-0" />}
                {label}
            </span>
            <span className="text-base tabular-nums text-black">{value}</span>
            {hint && (
                <span className={cn("truncate text-xs", tone === "danger" ? "text-red-800" : tone === "warning" ? "text-amber-800" : tone === "success" ? "text-green-800" : "text-gray-500")}>
                    {hint}
                </span>
            )}
        </div>
    );
}

export type MetricProps = React.ComponentProps<typeof Metric>;

/** A row of metrics: counters of a live queue, duration stats. */
export function MetricGrid({ metrics, columns }: { metrics: MetricProps[]; columns?: 2 | 3 | 4 | 5 | 6 }) {
    const cols = { 2: "sm:grid-cols-2", 3: "sm:grid-cols-3", 4: "sm:grid-cols-2 lg:grid-cols-4", 5: "sm:grid-cols-3 lg:grid-cols-5", 6: "sm:grid-cols-3 lg:grid-cols-6" }[columns ?? 4];
    return (
        <div className={cn("grid grid-cols-2 gap-2", cols)}>
            {metrics.map((metric, index) => (
                <Metric key={index} {...metric} />
            ))}
        </div>
    );
}

export type BarListRow = {
    key?: string;
    name: React.ReactNode;
    count: number;
    percentage: number;
    hint?: React.ReactNode;
    badge?: { label: string; tone: BadgeTone };
    /** Accessible name of a selectable row, when the visible text does not say what clicking does. */
    ariaLabel?: string;
};

/** Share of a whole, one single-color bar per row. The tone, if any, is carried by a badge, never by the bar. */
export function BarList({
    rows,
    onSelect,
    selected,
    scale = "max",
    valueLabel = row => `${row.count.toLocaleString("fr-FR")} · ${row.percentage.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`,
    meter = false
}: {
    rows: BarListRow[];
    onSelect?: (row: BarListRow) => void;
    selected?: string | null;
    /** `max`: the largest row fills the track; `total`: a bar is its share of 100 %. */
    scale?: "max" | "total";
    valueLabel?: (row: BarListRow) => string;
    /** Each bar is a gauge (a value against a ceiling): exposed as a `meter` named by `ariaLabel`. */
    meter?: boolean;
}) {
    const max = scale === "total" ? 100 : Math.max(1, ...rows.map(row => row.percentage));
    return (
        <ul className="flex flex-col gap-1">
            {rows.map((row, index) => {
                const key = row.key ?? String(index);
                const Row = onSelect ? "button" : "div";
                return (
                    <li key={key} data-key={key}>
                        <Row
                            type={onSelect ? "button" : undefined}
                            onClick={onSelect ? () => onSelect(row) : undefined}
                            aria-label={onSelect ? row.ariaLabel : undefined}
                            className={cn(
                                "flex w-full flex-col gap-1 rounded-sm px-2 py-1 text-left",
                                onSelect && "hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                                selected === key && "bg-gray-100"
                            )}
                        >
                            <span className="flex w-full items-center justify-between gap-2 text-sm">
                                <span className="flex min-w-0 items-center gap-2">
                                    <span className="truncate">{row.name}</span>
                                    {row.badge && <Badge tone={row.badge.tone}>{row.badge.label}</Badge>}
                                </span>
                                <span className="flex-shrink-0 tabular-nums text-black">{valueLabel(row)}</span>
                            </span>
                            <span
                                className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100"
                                {...(meter
                                    ? { role: "meter", "aria-label": row.ariaLabel, "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": Math.min(Math.max(row.percentage, 0), 100), "aria-valuetext": valueLabel(row) }
                                    : {})}
                            >
                                <span className="block h-full rounded-full bg-denim-blue" style={{ width: `${Math.min(Math.max((row.percentage / max) * 100, 0), 100)}%` }} />
                            </span>
                            {row.hint && <span className="text-xs text-gray-500">{row.hint}</span>}
                        </Row>
                    </li>
                );
            })}
        </ul>
    );
}

/**
 * Classes of a dense `ui/table` for rows that are not backend records (deployments, live
 * queue, aggregates). Records go through `ObjectListTableResizable layout="dense"` instead.
 */
export const denseTable = {
    header: "bg-gray-100",
    headerRow: "hover:bg-transparent",
    head: "h-8 text-xs font-normal text-gray-600",
    cell: "py-1.5 text-sm"
} as const;

/**
 * Header of a tool or satellite page: object tile, eyebrow, title, badges, actions. The tile
 * takes its icon and color from the object's `model_metadata` (`ObjectConfig.icon` / `color`).
 */
export function PageHeader({
    objectKey,
    icon,
    eyebrow,
    title,
    titleId,
    badges,
    actions,
    children
}: {
    /** URL key of the object config (`dialer-workflows`), used to read its metadata. */
    objectKey?: string;
    /** Fallback icon while the metadata loads, or for a page without an object. */
    icon?: string;
    eyebrow?: React.ReactNode;
    title: React.ReactNode;
    titleId?: string;
    badges?: React.ReactNode;
    actions?: React.ReactNode;
    children?: React.ReactNode;
}) {
    // Read through `useCRM` rather than `useCRMMetadata`: a page rendered outside the CRM
    // provider (standalone tools, tests) keeps the neutral tile instead of crashing.
    const crm = useCRM() as ReturnType<typeof useCRM> | null;
    const objectMetadata = objectKey ? crm?.metadataCache[objectKey]?.model_metadata : undefined;
    React.useEffect(() => {
        if (objectKey && crm && crm.metadataCache[objectKey] === undefined) crm.ensureLoaded(objectKey).catch(() => undefined);
    }, [objectKey, crm]);
    const Icon = resolveIcon(objectMetadata?.icon ?? icon);
    return (
        <Panel>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="flex min-w-0 flex-1 items-center gap-4">
                    <div
                        className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-md bg-gray-500"
                        style={objectMetadata?.color ? { backgroundColor: objectMetadata.color } : undefined}
                    >
                        {Icon && <Icon className="h-8 w-8 text-white" strokeWidth={1.5} />}
                    </div>
                    <div className="min-w-0">
                        {eyebrow && <p className="truncate text-sm font-medium text-gray-500">{eyebrow}</p>}
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 id={titleId} className="truncate text-2xl text-black">
                                {title}
                            </h1>
                            {badges}
                        </div>
                    </div>
                </div>
                {actions && <div className="flex flex-wrap gap-2 sm:max-w-[60%] sm:justify-end">{actions}</div>}
            </div>
            {children}
        </Panel>
    );
}

/** Loading, empty and error states of a panel, table or tab. */
export function StateBlock({
    kind = "empty",
    message,
    onRetry,
    action,
    className
}: {
    kind?: "loading" | "empty" | "error";
    message?: React.ReactNode;
    onRetry?: () => void;
    action?: React.ReactNode;
    className?: string;
}) {
    const text = message ?? (kind === "loading" ? "Loading..." : kind === "error" ? "Unable to load this content." : "No result found");
    return (
        <div className={cn("flex flex-col items-center justify-center gap-2 py-8 text-sm text-gray-500", className)} role={kind === "error" ? "alert" : "status"}>
            <div className="flex items-center gap-2">
                {kind === "loading" ? <Loader className="h-5 w-5 animate-spin" /> : kind === "error" ? <AlertCircle className="h-5 w-5" /> : <Inbox className="h-5 w-5" />}
                <span>{text}</span>
            </div>
            {kind === "error" && onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                    <RotateCw className="mr-1 h-4 w-4" />
                    Retry
                </Button>
            )}
            {action}
        </div>
    );
}

/**
 * Status of a configurable feature: state badge, key facts, links and one or two actions. With actions or
 * links it reads on two lines — the state and what you can do to it, then the facts — so the buttons never
 * wrap alone under the facts in a half-width column; without, one line.
 */
export function SummaryStrip({
    status,
    note,
    items = [],
    links,
    actions
}: {
    status?: { label: string; tone?: BadgeTone };
    note?: React.ReactNode;
    items?: Array<{ label: React.ReactNode; value?: React.ReactNode; href?: string }>;
    links?: React.ReactNode;
    actions?: React.ReactNode;
}) {
    const state = (status || note) && (
        <div className="flex items-center gap-2">
            {status && <Badge tone={status.tone ?? "neutral"}>{status.label}</Badge>}
            {note && <span className="text-xs text-gray-500">{note}</span>}
        </div>
    );
    const facts = items.length > 0 && (
        <dl className="flex flex-wrap items-center gap-x-6 gap-y-1">
            {items.map((item, index) => (
                <div key={index} className="flex flex-col">
                    <dt className="text-xs text-gray-600">{item.label}</dt>
                    <dd className="text-sm tabular-nums">
                        {isEmptyNode(item.value) ? (
                            <EmptyValue />
                        ) : item.href ? (
                            <Link className="text-blue-700 hover:text-blue-900 hover:underline" to={item.href}>
                                {item.value}
                            </Link>
                        ) : (
                            item.value
                        )}
                    </dd>
                </div>
            ))}
        </dl>
    );
    if (!links && !actions) {
        return (
            <div className="flex w-full flex-wrap items-center gap-x-6 gap-y-2 border-b pb-3">
                {state}
                {facts}
            </div>
        );
    }
    return (
        <div className="flex w-full flex-col gap-3 border-b pb-3" data-testid="summary-strip">
            <div className="flex flex-wrap items-center justify-between gap-2">
                {state || <span />}
                <div className="flex flex-wrap items-center gap-2">
                    {links}
                    {actions}
                </div>
            </div>
            {facts}
        </div>
    );
}

export type ChecklistItem = { code?: string; label: React.ReactNode; done: boolean; hint?: React.ReactNode };

/** Steps left before an object is ready (put in service, publish). Done steps are checked, never struck through. */
export function Checklist({ title, items, footer }: { title?: React.ReactNode; items: ChecklistItem[]; footer?: React.ReactNode }) {
    const done = items.filter(item => item.done).length;
    return (
        <div className="flex flex-col gap-2">
            {title && (
                <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-black">{title}</span>
                    <span className="text-xs text-gray-500">
                        {done} / {items.length} done
                    </span>
                </div>
            )}
            <ol className="flex flex-col">
                {items.map((item, index) => (
                    <li key={item.code ?? index} className="flex items-start gap-2 border-b py-1.5 text-sm">
                        {item.done ? <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-800" /> : <Circle className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />}
                        <div className="flex min-w-0 flex-col">
                            <span className={cn(item.done ? "text-gray-600" : "text-black")}>{item.label}</span>
                            {item.hint && !item.done && <span className="text-xs text-gray-500">{item.hint}</span>}
                        </div>
                    </li>
                ))}
            </ol>
            {footer}
        </div>
    );
}

/** Back to the parent view of a drill-down page inside a tab. */
export function BackLink({ label = "Back", to, onClick }: { label?: React.ReactNode; to?: string; onClick?: () => void }) {
    const className = "inline-flex items-center gap-1 text-sm text-blue-700 hover:text-blue-900 hover:underline";
    const content = (
        <>
            <ArrowLeft className="h-4 w-4" />
            {label}
        </>
    );
    return to ? (
        <Link to={to} className={className}>
            {content}
        </Link>
    ) : (
        <button type="button" className={className} onClick={onClick}>
            {content}
        </button>
    );
}

/** Two lines in one table cell: the value, then a muted (or warning/danger) secondary line. */
export function CellStack({ primary, secondary, secondaryTone }: { primary: React.ReactNode; secondary?: React.ReactNode; secondaryTone?: "warning" | "danger" }) {
    return (
        <div className="flex min-w-0 flex-col">
            <span className="truncate">{primary}</span>
            {secondary && <span className={cn("truncate text-xs", secondaryTone === "danger" ? "text-red-800" : secondaryTone === "warning" ? "text-amber-800" : "text-gray-500")}>{secondary}</span>}
        </div>
    );
}

export type Note = { text: React.ReactNode; tone?: "warning" | "danger" };

/** Short remarks under a value (a table cell's « Détail »): muted, or warning / danger. */
export function NoteList({ notes }: { notes: Note[] }) {
    if (notes.length === 0) return <EmptyValue />;
    return (
        <ul className="flex flex-col gap-0.5 text-sm">
            {notes.map((note, index) => (
                <li key={index} className={note.tone === "danger" ? "text-red-800" : note.tone === "warning" ? "text-amber-800" : "text-gray-600"}>
                    {note.text}
                </li>
            ))}
        </ul>
    );
}

/** Inset: quoted description, expanded row, secondary block inside a panel. */
export function Inset({ className, children }: { className?: string; children: React.ReactNode }) {
    return <div className={cn("rounded-sm border bg-gray-50 p-2 text-sm", className)}>{children}</div>;
}

export type SegmentedOption<T extends string> = { value: T; label: React.ReactNode; icon?: string };

/**
 * Switches the view of one content (period, scope), never the route: navigation between
 * screens of a record is one row of `NavTabs` plus drill-downs. Keyboard: arrows move the
 * selection, like a radio group.
 */
export function SegmentedControl<T extends string>({
    value,
    options,
    onChange,
    ariaLabel,
    size = "sm"
}: {
    value: T;
    options: SegmentedOption<T>[];
    onChange: (value: T) => void;
    ariaLabel: string;
    size?: "sm" | "md";
}) {
    const refs = React.useRef<Array<HTMLButtonElement | null>>([]);
    const move = (index: number) => {
        const next = (index + options.length) % options.length;
        onChange(options[next].value);
        refs.current[next]?.focus();
    };
    return (
        <div role="radiogroup" aria-label={ariaLabel} className={cn("inline-flex items-center rounded-lg bg-gray-100 p-0.5", size === "sm" ? "h-8" : "h-9")}>
            {options.map((option, index) => {
                const selected = option.value === value;
                const Icon = resolveIcon(option.icon);
                return (
                    <button
                        key={option.value}
                        ref={element => {
                            refs.current[index] = element;
                        }}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        tabIndex={selected ? 0 : -1}
                        onClick={() => onChange(option.value)}
                        onKeyDown={event => {
                            if (event.key === "ArrowRight" || event.key === "ArrowDown") {
                                event.preventDefault();
                                move(index + 1);
                            } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
                                event.preventDefault();
                                move(index - 1);
                            }
                        }}
                        className={cn(
                            "inline-flex h-full items-center gap-1 whitespace-nowrap rounded-md px-3 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                            selected ? "bg-white text-black shadow-sm" : "text-gray-600 hover:text-black"
                        )}
                    >
                        {Icon && <Icon className="h-3.5 w-3.5" />}
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}
